import * as React from 'react';
import {Box, Fab, Tooltip} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import {CHAT_UI_URL} from '../../config/apiConfig';

interface AgenticChatProps {
    token: string | null;
    apiUrl: string;
    module: string;
    /** URL of the agentic chat app. Defaults to http://localhost:3001 */
    chatUrl?: string;
}

export default function AgenticChat({
                                        token,
                                        apiUrl,
                                        module,
                                        chatUrl = CHAT_UI_URL,
                                    }: AgenticChatProps): React.ReactElement {
    const {t} = useTranslation();
    const [open, setOpen] = React.useState(false);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);

    const sendConfig = React.useCallback(() => {
        const frame = iframeRef.current;
        if (!frame || !token) return;

        frame.contentWindow?.postMessage(
            {
                type: 'AGENTIC_CONFIG',
                payload: {token, apiUrl, module},
            },
            chatUrl,
        );
    }, [token, apiUrl, module, chatUrl]);

    // Listen for close message from iframe
    React.useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Security: validate origin matches chatUrl
            try {
                const chatOrigin = new URL(chatUrl).origin;
                if (event.origin !== chatOrigin) {
                    return;
                }
            } catch {
                // If chatUrl is invalid, skip validation
            }

            // Handle close request from iframe
            if (event.data?.type === 'AGENTIC_CLOSE') {
                setOpen(false);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [chatUrl]);

    return (
        <>
            {/* Toggle button */}
            <Tooltip title={open ? t('employee.chat.closeTooltip') : t('employee.chat.openTooltip')} placement="left">
                <Fab
                    color="primary"
                    aria-label="AI chat assistant"
                    onClick={() => setOpen((prev) => !prev)}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 10000,
                    }}
                >
                    {open ? <CloseIcon/> : <ChatIcon/>}
                </Fab>
            </Tooltip>

            {/* Chat iframe panel */}
            {open && (
                <Box
                    sx={{
                        position: 'fixed',
                        // Mobile: full screen, Desktop: bottom-right panel
                        bottom: {xs: 0, sm: 88},
                        right: {xs: 0, sm: 24},
                        left: {xs: 0, sm: 'auto'},
                        top: {xs: 0, sm: 'auto'},
                        width: {xs: '100%', sm: 420},
                        height: {xs: '100%', sm: 620},
                        maxHeight: {xs: '100vh', sm: 'calc(100vh - 112px)'},
                        borderRadius: {xs: 0, sm: '12px'},
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        overflow: 'hidden',
                        zIndex: 9999,
                        bgcolor: 'background.paper',
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        src={chatUrl}
                        title={t('employee.chat.iframeTitle')}
                        onLoad={sendConfig}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                    />
                </Box>
            )}
        </>
    );
}
